/*
  menara-fiche-tableau.js — fiche produit des tableaux (sections/menara-fiche-tableau.liquid).

  Les données viennent de Liquid : variantes réelles, prix déjà formatés par la
  boutique. Ce script les accorde à l'interface :
    - cartes de format et de finition, combinaisons impossibles désactivées ;
    - variante choisie, prix, récapitulatif, adresse ?variant= ;
    - visuels, zoom, barre d'achat du téléphone.
  L'ajout au panier reste celui du thème (product-form-component) : ce script
  se contente de bloquer l'envoi tant qu'aucune variante vendable n'est choisie.
*/
import { StandardEvents } from '@shopify/events';

const TELEPHONE = window.matchMedia('(max-width: 989px)');
const POINTEUR_FIN = window.matchMedia('(hover: hover) and (pointer: fine)');
const MOUVEMENT_REDUIT = window.matchMedia('(prefers-reduced-motion: reduce)');

const echapper = (t) =>
  String(t).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
const remplir = (modele, valeurs) =>
  Object.entries(valeurs).reduce((t, [cle, v]) => t.split(`[${cle}]`).join(v), modele || '');
const borner = (n, min, max) => Math.min(max, Math.max(min, n));

class MenaraFicheTableau extends HTMLElement {
  connectedCallback() {
    if (this.demarre) return;
    const source = this.querySelector('[data-mft-donnees]');
    if (!source) return;
    try {
      this.d = JSON.parse(source.textContent);
    } catch (erreur) {
      console.warn('[fiche tableau] données illisibles', erreur);
      return;
    }
    this.demarre = true;
    this.nettoyeurs = [];

    this.variantes = this.d.variantes || [];
    this.L = this.d.libelles || {};
    this.nbOptions = (this.d.options || []).length;
    this.radios = Array.from(this.querySelectorAll('[data-mft-option]'));
    this.types = {};
    this.querySelectorAll('[data-mft-etape]').forEach((etape) => {
      this.types[etape.dataset.mftEtape] = etape.dataset.type;
    });
    this.form = this.querySelector('form');
    this.champId = this.querySelector('[data-mft-id]');
    this.recap = this.querySelector('[data-mft-recap]');
    this.ajustement = this.querySelector('[data-mft-ajustement]');
    this.prix = Array.from(this.querySelectorAll('[data-mft-prix]'));
    this.bouton = this.querySelector('[data-mft-ajouter]');
    this.boutonTexte = this.querySelector('[data-mft-ajouter-texte]');
    this.annonce = this.querySelector('[data-mft-annonce]');

    // Rang d'affichage de chaque valeur : départage les ajustements automatiques.
    this.rang = new Map();
    this.radios.forEach((r, k) => this.rang.set(`${r.dataset.mftOption}|${r.value}`, k));

    this.brancherVisuels();
    this.brancherBarre();
    this.brancherOptions();
    this.brancherQuantite();
    this.brancherPanier();
    this.brancherEditeur();
    this.appliquer({ annoncer: false, url: false });
  }

  disconnectedCallback() {
    (this.nettoyeurs || []).forEach((f) => f());
    this.nettoyeurs = [];
    this.demarre = false;
  }

  ecouter(cible, type, fn, options) {
    if (!cible) return;
    cible.addEventListener(type, fn, options);
    this.nettoyeurs.push(() => cible.removeEventListener(type, fn, options));
  }

  /* — Sélection — */

  selection() {
    const sel = new Array(this.nbOptions).fill(null);
    this.radios.forEach((r) => {
      if (r.checked) sel[Number(r.dataset.mftOption)] = r.value;
    });
    return sel;
  }

  exacte(sel) {
    if (sel.includes(null)) return null;
    return this.variantes.find((v) => v.options.every((val, j) => sel[j] === val)) || null;
  }

  variante() {
    if (this.d.sansOption) return this.variantes[0] || null;
    return this.exacte(this.selection());
  }

  /** La variante respecte-t-elle la sélection, en ignorant l'option « sauf » ? */
  accorde(v, sel, sauf) {
    return v.options.every((val, j) => j === sauf || sel[j] === null || sel[j] === val);
  }

  cocher(sel) {
    this.radios.forEach((r) => {
      r.checked = sel[Number(r.dataset.mftOption)] === r.value;
    });
  }

  brancherOptions() {
    this.radios.forEach((r) =>
      this.ecouter(r, 'change', () => {
        if (r.checked) this.choisir(Number(r.dataset.mftOption), r.value);
      })
    );

    // Tant qu'aucune variante vendable n'est choisie, l'envoi n'atteint pas
    // le composant du thème. Le thème délègue ses « on:submit » au document,
    // en capture : cette garde écoute la fenêtre, qui passe avant.
    this.ecouter(
      window,
      'submit',
      (e) => {
        if (e.target !== this.form) return;
        const v = this.variante();
        if (v && v.dispo) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        if (!v) this.allerAuChoix();
      },
      true
    );
  }

  choisir(i, valeur) {
    const sel = this.selection();
    sel[i] = valeur;
    let ajuste = null;

    if (!sel.includes(null) && !this.exacte(sel)) {
      // Garder le choix du client, conserver le plus possible des autres
      // options, préférer une variante disponible, puis l'ordre d'affichage.
      const score = (v) =>
        v.options.reduce((t, val, j) => t + (j !== i && val === sel[j] ? 100 : 0), 0) + (v.dispo ? 10 : 0);
      const rang = (v) =>
        v.options.reduce((t, val, j) => t + (j === i ? 0 : this.rang.get(`${j}|${val}`) ?? 999), 0);
      const retenue = this.variantes
        .filter((v) => v.options[i] === valeur)
        .sort((a, b) => score(b) - score(a) || rang(a) - rang(b))[0];

      if (retenue) {
        retenue.options.forEach((val, j) => {
          if (j !== i && sel[j] !== val) ajuste = { j, valeur: val };
          sel[j] = val;
        });
        this.cocher(sel);
      }
    }
    this.appliquer({ annoncer: true, ajuste });
  }

  majCartes(sel) {
    this.radios.forEach((r) => {
      const i = Number(r.dataset.mftOption);
      const carte = r.nextElementSibling;
      if (!carte) return;

      const avec = this.variantes.filter((v) => v.options[i] === r.value);
      const compatibles = avec.filter((v) => this.accorde(v, sel, i));
      const compatible = compatibles.length > 0;
      const epuise = compatible && !compatibles.some((v) => v.dispo);

      // Le premier choix (le format) reste toujours accessible : le changer
      // ajuste les options suivantes. Une option suivante impossible se désactive.
      r.disabled = i > 0 && !compatible && !r.checked;
      carte.classList.toggle('est-incompatible', !compatible);
      carte.classList.toggle('est-epuise', epuise);

      let texteBadge = carte.dataset.badgeFixe || '';
      let seule = false;
      if (!texteBadge && this.nbOptions === 2 && this.types[i] === 'format') {
        const autres = [...new Set(avec.map((v) => v.options[1 - i]))];
        if (autres.length === 1) {
          texteBadge = remplir(this.L.seulement, { valeur: autres[0] });
          seule = true;
        }
      }
      const badge = carte.querySelector('[data-mft-badge]');
      if (badge) {
        badge.textContent = texteBadge;
        badge.hidden = !texteBadge;
      }

      let texteNote = '';
      if (!compatible && !seule) {
        const autres = sel.filter((val, j) => j !== i && val !== null);
        if (autres.length) {
          const quoi = i === 0 ? autres.join(', ').toLowerCase() : autres.join(', ');
          texteNote = remplir(this.L.nonPropose, { valeur: quoi });
        }
      } else if (epuise) {
        texteNote = this.L.epuise;
      }
      const note = carte.querySelector('[data-mft-note]');
      if (note) note.textContent = texteNote;
    });
  }

  appliquer({ annoncer = true, ajuste = null, url = true } = {}) {
    const sel = this.selection();
    const complete = this.d.sansOption || !sel.includes(null);
    const v = this.variante();
    this.majCartes(sel);

    this.querySelectorAll('[data-mft-etape]').forEach((etape) => {
      const libelle = etape.querySelector('[data-mft-valeur]');
      if (libelle) libelle.textContent = sel[Number(etape.dataset.mftEtape)] || '';
    });

    let etat = 'ok';
    if (!complete) etat = 'choisir';
    else if (!v) etat = 'indisponible';
    else if (!v.dispo) etat = 'epuise';
    this.dataset.etat = etat;
    const textes = { ok: this.L.ajouter, choisir: this.L.choisir, indisponible: this.L.indisponible, epuise: this.L.epuise };

    if (this.champId) this.champId.value = v ? v.id : '';
    if (this.bouton) {
      this.bouton.disabled = etat === 'indisponible' || etat === 'epuise';
      this.bouton.setAttribute('aria-disabled', etat === 'ok' ? 'false' : 'true');
    }
    if (this.boutonTexte) this.boutonTexte.textContent = textes[etat];

    const config = this.d.sansOption ? '' : complete ? sel.join(' / ') : this.L.choisir;
    if (this.recap && !this.d.sansOption) {
      this.recap.textContent = config;
      this.recap.dataset.varianteId = v ? String(v.id) : '';
      this.recap.dataset.etat = etat;
      Object.entries(this.types).forEach(([j, type]) => {
        if (type === 'format' || type === 'finition') this.recap.dataset[type] = sel[j] || '';
      });
    }

    const attente = etat === 'indisponible' ? this.L.indisponible : this.L.choisir;
    const htmlPrix = v ? v.prix : `<span class="mft__prix-attente">${echapper(attente)}</span>`;
    this.prix.forEach((el) => {
      el.innerHTML = htmlPrix;
    });

    if (this.ajustement) {
      if (ajuste) {
        this.ajustement.textContent = remplir(this.L.ajuste, {
          option: this.d.options[ajuste.j] || '',
          valeur: ajuste.valeur,
        });
        this.ajustement.hidden = false;
      } else {
        this.ajustement.hidden = true;
        this.ajustement.textContent = '';
      }
    }

    this.majBarre(v, etat, config, textes[etat]);

    if (url && v && complete && !this.d.sansOption) {
      const adresse = new URL(window.location.href);
      if (adresse.searchParams.get('variant') !== String(v.id)) {
        adresse.searchParams.set('variant', v.id);
        window.history.replaceState(window.history.state, '', adresse.toString());
      }
    }
    if (v && v.media) this.montrerMedia(v.media);

    if (annoncer) {
      const morceaux = [config, v ? v.prixTexte : '', etat === 'ok' ? '' : textes[etat]];
      if (ajuste && this.ajustement) morceaux.push(this.ajustement.textContent);
      this.dire(morceaux.filter(Boolean).join(' — '));
    }
  }

  dire(texte) {
    if (!this.annonce) return;
    this.annonce.textContent = '';
    requestAnimationFrame(() => {
      this.annonce.textContent = texte;
    });
  }

  allerAuChoix() {
    const sel = this.selection();
    const i = Math.max(0, sel.findIndex((x) => x === null));
    const etape = this.querySelector(`[data-mft-etape="${i}"]`);
    if (!etape) return;
    etape.scrollIntoView({ behavior: MOUVEMENT_REDUIT.matches ? 'auto' : 'smooth', block: 'center' });
    const premier = etape.querySelector('.mft__radio:not(:disabled)');
    if (premier) premier.focus({ preventScroll: true });
    etape.classList.remove('est-a-choisir');
    requestAnimationFrame(() => etape.classList.add('est-a-choisir'));
    setTimeout(() => etape.classList.remove('est-a-choisir'), 1600);
  }

  /* — Quantité — */

  brancherQuantite() {
    const champ = this.querySelector('[data-mft-qte]');
    if (!champ) return;
    const lire = () => parseInt(champ.value, 10) || 1;
    this.ecouter(this.querySelector('[data-mft-moins]'), 'click', () => {
      champ.value = borner(lire() - 1, 1, 99);
    });
    this.ecouter(this.querySelector('[data-mft-plus]'), 'click', () => {
      champ.value = borner(lire() + 1, 1, 99);
    });
    this.ecouter(champ, 'change', () => {
      champ.value = borner(lire(), 1, 99);
    });
  }

  /* — Visuels et zoom — */

  brancherVisuels() {
    this.vues = Array.from(this.querySelectorAll('[data-mft-vue]'));
    this.vignettes = Array.from(this.querySelectorAll('[data-mft-vignette]'));
    this.vueActive = 0;
    this.vignettes.forEach((b, i) => this.ecouter(b, 'click', () => this.montrerVue(i)));

    this.scene = this.querySelector('[data-mft-scene]');
    this.boutonZoom = this.querySelector('[data-mft-zoom]');
    this.loupe = this.querySelector('[data-mft-loupe]');
    this.dialogue = this.querySelector('[data-mft-dialogue]');
    if (!this.boutonZoom) return;

    this.ecouter(this.boutonZoom, 'click', (e) => {
      e.stopPropagation();
      this.basculerZoom();
    });
    this.ecouter(this.scene, 'click', (e) => {
      if (e.target.closest('button')) return;
      if (this.loupeActive) this.quitterLoupe(false);
      else if (this.loupePossible()) this.basculerZoom(e);
    });

    if (this.loupe) {
      this.ecouter(this.loupe, 'pointermove', (e) => this.placerLoupe(e.clientX, e.clientY));
      this.ecouter(this.loupe.querySelector('[data-mft-loupe-fermer]'), 'click', (e) => {
        e.stopPropagation();
        this.quitterLoupe(true);
      });
      this.ecouter(this, 'keydown', (e) => {
        if (!this.loupeActive) return;
        if (e.key === 'Escape') {
          e.preventDefault();
          this.quitterLoupe(true);
          return;
        }
        const pas = { ArrowLeft: [-8, 0], ArrowRight: [8, 0], ArrowUp: [0, -8], ArrowDown: [0, 8] }[e.key];
        if (pas) {
          e.preventDefault();
          this.pos = [borner(this.pos[0] + pas[0], 0, 100), borner(this.pos[1] + pas[1], 0, 100)];
          this.peindreLoupe();
        }
      });
    }

    if (this.dialogue) {
      const cadre = this.dialogue.querySelector('.mft__dialogue-cadre');
      const image = this.dialogue.querySelector('[data-mft-dialogue-img]');
      this.ecouter(this.dialogue.querySelector('[data-mft-dialogue-fermer]'), 'click', () => this.dialogue.close());
      this.ecouter(this.dialogue, 'click', (e) => {
        if (e.target === this.dialogue) this.dialogue.close();
      });
      this.ecouter(this.dialogue, 'close', () => {
        this.boutonZoom.setAttribute('aria-pressed', 'false');
        this.boutonZoom.focus({ preventScroll: true });
      });
      // Image agrandie : on se place au centre, le doigt parcourt le reste.
      this.ecouter(image, 'load', () => {
        if (!cadre || image.dataset.centrer !== 'oui') return;
        image.dataset.centrer = 'non';
        cadre.scrollLeft = (cadre.scrollWidth - cadre.clientWidth) / 2;
        cadre.scrollTop = (cadre.scrollHeight - cadre.clientHeight) / 2;
      });
    }
  }

  montrerVue(i) {
    if (!this.vues[i]) return;
    this.quitterLoupe(false);
    this.vueActive = i;
    this.vues.forEach((vue, k) => {
      vue.hidden = k !== i;
      vue.classList.toggle('est-active', k === i);
    });
    this.vignettes.forEach((b, k) => {
      b.classList.toggle('est-active', k === i);
      b.setAttribute('aria-pressed', k === i ? 'true' : 'false');
    });
  }

  montrerMedia(id) {
    const i = this.vues.findIndex((vue) => vue.dataset.mediaId === String(id));
    if (i >= 0 && i !== this.vueActive) this.montrerVue(i);
  }

  loupePossible() {
    return Boolean(this.loupe) && POINTEUR_FIN.matches && !TELEPHONE.matches;
  }

  basculerZoom(e) {
    const vue = this.vues[this.vueActive];
    const source = vue && vue.dataset.zoom;
    if (!source) return;

    if (this.loupePossible()) {
      if (this.loupeActive) {
        this.quitterLoupe(true);
        return;
      }
      // L'image déjà affichée sert de relais le temps que la grande arrive.
      const affichee = vue.querySelector('img');
      const relais = affichee && (affichee.currentSrc || affichee.src);
      this.loupe.style.backgroundImage = `url("${relais || source}")`;
      if (relais) {
        const grande = new Image();
        grande.onload = () => {
          if (this.loupeActive && this.vues[this.vueActive] === vue) this.loupe.style.backgroundImage = `url("${source}")`;
        };
        grande.src = source;
      }
      this.loupe.hidden = false;
      this.loupeActive = true;
      this.scene.classList.add('est-zoom');
      this.boutonZoom.setAttribute('aria-pressed', 'true');
      if (e) this.placerLoupe(e.clientX, e.clientY);
      else {
        this.pos = [50, 50];
        this.peindreLoupe();
      }
      const fermer = this.loupe.querySelector('[data-mft-loupe-fermer]');
      if (fermer) fermer.focus({ preventScroll: true });
    } else if (this.dialogue) {
      // Même relais qu'à la loupe : l'image affichée, puis la grande.
      const image = this.dialogue.querySelector('[data-mft-dialogue-img]');
      const affichee = vue.querySelector('img');
      const relais = affichee && (affichee.currentSrc || affichee.src);
      image.alt = vue.dataset.alt || '';
      image.dataset.centrer = 'oui';
      image.src = relais || source;
      if (relais) {
        const grande = new Image();
        grande.onload = () => {
          if (this.dialogue.open) image.src = source;
        };
        grande.src = source;
      }
      this.boutonZoom.setAttribute('aria-pressed', 'true');
      this.dialogue.showModal();
    }
  }

  placerLoupe(x, y) {
    const r = this.scene.getBoundingClientRect();
    this.pos = [borner(((x - r.left) / r.width) * 100, 0, 100), borner(((y - r.top) / r.height) * 100, 0, 100)];
    this.peindreLoupe();
  }

  peindreLoupe() {
    this.loupe.style.backgroundPosition = `${this.pos[0].toFixed(1)}% ${this.pos[1].toFixed(1)}%`;
  }

  quitterLoupe(rendreFocus) {
    if (!this.loupeActive) return;
    this.loupe.hidden = true;
    this.loupeActive = false;
    this.scene.classList.remove('est-zoom');
    this.boutonZoom.setAttribute('aria-pressed', 'false');
    if (rendreFocus) this.boutonZoom.focus({ preventScroll: true });
  }

  /* — Barre d'achat du téléphone — */

  brancherBarre() {
    this.barre = this.querySelector('[data-mft-barre]');
    const boutonPrincipal = this.querySelector('[data-mft-ajouter]');
    if (!this.barre || !boutonPrincipal) return;
    this.barreConfig = this.barre.querySelector('[data-mft-barre-config]');
    this.barrePrix = this.barre.querySelector('[data-mft-barre-prix]');
    this.barreBouton = this.barre.querySelector('[data-mft-barre-bouton]');
    this.barreTexte = this.barre.querySelector('[data-mft-barre-bouton-texte]');

    let depasse = false;
    let piedVisible = false;
    const maj = () => {
      const montrer = TELEPHONE.matches && depasse && !piedVisible;
      if (montrer) this.caleBarre();
      this.barre.classList.toggle('est-visible', montrer);
      this.barre.toggleAttribute('inert', !montrer);
      this.barre.setAttribute('aria-hidden', montrer ? 'false' : 'true');
    };

    // Mesure au défilement, quel que soit le conteneur qui défile (fenêtre ou
    // .page-wrapper du thème) : l'événement est écouté en capture sur le
    // document, et la mesure n'a lieu qu'une fois par image.
    const pied = document.querySelector('.shopify-section-group-footer-group, footer');
    let image = 0;
    const mesurer = () => {
      image = 0;
      // L'en-tête du site est fixe : un bouton passé dessous est déjà dépassé.
      const entete = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--me-hauteur')) || 0;
      depasse = boutonPrincipal.getBoundingClientRect().bottom < entete;
      // Le pied de page est haut : la barre ne s'efface que lorsqu'il occupe la
      // moitié basse de l'écran, pas dès que sa première ligne apparaît.
      piedVisible = pied ? pied.getBoundingClientRect().top < window.innerHeight * 0.5 : false;
      maj();
    };
    const planifier = () => {
      if (!image) image = requestAnimationFrame(mesurer);
    };
    this.ecouter(document, 'scroll', planifier, { capture: true, passive: true });
    this.ecouter(window, 'resize', planifier, { passive: true });
    this.ecouter(TELEPHONE, 'change', planifier);
    this.ecouter(this.barreBouton, 'click', () => {
      const v = this.variante();
      if (v && v.dispo && this.form) this.form.requestSubmit(boutonPrincipal);
      else if (!v) this.allerAuChoix();
    });
    this.nettoyeurs.push(() => cancelAnimationFrame(image));
    mesurer();
  }

  /** Place la barre au-dessus de la navigation basse du site, si elle est affichée. */
  caleBarre() {
    const basse = document.querySelector('[data-me-basse]');
    let bas = 0;
    if (basse && getComputedStyle(basse).display !== 'none') {
      const r = basse.getBoundingClientRect();
      if (r.height > 0 && r.top < window.innerHeight) bas = Math.max(0, window.innerHeight - r.top);
    }
    this.barre.style.setProperty('--mft-barre-bas', `${Math.round(bas)}px`);
  }

  majBarre(v, etat, config, texte) {
    if (!this.barre) return;
    if (this.barreConfig) this.barreConfig.textContent = config;
    if (this.barrePrix) this.barrePrix.textContent = v ? v.prixTexte : '';
    if (this.barreTexte) this.barreTexte.textContent = texte;
    if (this.barreBouton) this.barreBouton.disabled = etat === 'indisponible' || etat === 'epuise';
  }

  /* — Panier — */

  brancherPanier() {
    const composant = this.querySelector('product-form-component');
    if (!composant) return;
    this.ecouter(document, StandardEvents.cartLinesUpdate, (e) => {
      if (!(e.target instanceof Node) || !composant.contains(e.target)) return;
      if (this.bouton) this.bouton.classList.add('est-en-cours');
      Promise.resolve(e.promise)
        .then((resultat) => {
          if (this.bouton) this.bouton.classList.remove('est-en-cours');
          if (resultat && resultat.detail && resultat.detail.didError) {
            this.dire(this.L.erreur);
            return;
          }
          this.dire(this.L.ajoute);
          // Le tiroir panier ne s'ouvre que si le thème le prévoit : la fiche
          // confirme donc l'ajout elle-même, sur le bouton et sous le bouton.
          if (this.bouton) {
            this.bouton.classList.add('est-ajoute');
            if (this.boutonTexte) this.boutonTexte.textContent = this.L.ajoute;
            clearTimeout(this.minuteurAjout);
            this.minuteurAjout = setTimeout(() => {
              if (!this.bouton) return;
              this.bouton.classList.remove('est-ajoute');
              this.appliquer({ annoncer: false, url: false });
            }, 2200);
          }
          const confirmation = this.querySelector('[data-mft-confirmation]');
          if (confirmation) {
            confirmation.hidden = false;
            clearTimeout(this.minuteurConfirmation);
            this.minuteurConfirmation = setTimeout(() => {
              confirmation.hidden = true;
            }, 9000);
          }
          // Le compteur de l'en-tête Menara se met à jour sur cet événement.
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        })
        .catch(() => {
          if (this.bouton) this.bouton.classList.remove('est-en-cours');
        });
    });
  }

  /* — Éditeur de thème — */

  brancherEditeur() {
    if (!(window.Shopify && window.Shopify.designMode)) return;
    this.ecouter(document, 'shopify:block:select', (e) => {
      if (!this.contains(e.target)) return;
      const volet = e.target.closest && e.target.closest('[data-mft-volet]');
      if (volet) volet.open = true;
    });
  }
}

if (!customElements.get('menara-fiche-tableau')) {
  customElements.define('menara-fiche-tableau', MenaraFicheTableau);
}
